import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";

export interface SignatureBookForm {
  title: string;
  client_id: string;
  contract_id: string;
  job_id: string;
  event_date: string;
  status: string;
  guest_book_enabled: boolean;
  closing_message: string;
}

interface Client { id: string; full_name: string; }
interface Job    { id: string; title: string; }
interface Contract { id: string; title?: string; event_date?: string | null; }

interface Props {
  tenantId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}

const BLANK: SignatureBookForm = {
  title: "",
  client_id: "",
  contract_id: "",
  job_id: "",
  event_date: "",
  status: "draft",
  guest_book_enabled: false,
  closing_message: "",
};

export default function SignatureBookModal({ tenantId, onClose, onCreated }: Props) {
  const [form, setForm]         = useState<SignatureBookForm>(BLANK);
  const [clients, setClients]   = useState<Client[]>([]);
  const [jobs, setJobs]         = useState<Job[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      const [cr, jr, co] = await Promise.all([
        supabase.from("clients").select("id, full_name").eq("tenant_id", tenantId).eq("status", "active").order("full_name"),
        supabase.from("jobs").select("id, title").eq("tenant_id", tenantId).order("title"),
        supabase.from("contracts").select("id, title, event_date").eq("tenant_id", tenantId).order("created_at", { ascending: false }),
      ]);
      if (cr.data)  setClients(cr.data as Client[]);
      if (jr.data)  setJobs(jr.data as Job[]);
      if (co.data)  setContracts(co.data as Contract[]);
    };
    load();
  }, [tenantId]);

  const set = <K extends keyof SignatureBookForm>(k: K, v: SignatureBookForm[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const generateSlug = (title: string) => {
    const base = title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");
    return `${base}-${Date.now().toString(36)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setError("El título es obligatorio."); return; }
    if (!form.client_id)    { setError("Selecciona un cliente."); return; }
    setSaving(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("signature_books")
      .insert({
        tenant_id:           tenantId,
        client_id:           form.client_id,
        contract_id:         form.contract_id || null,
        job_id:              form.job_id || null,
        title:               form.title.trim(),
        event_date:          form.event_date || null,
        status:              form.status,
        guest_book_enabled:  form.guest_book_enabled,
        closing_message:     form.closing_message || null,
        public_slug:         generateSlug(form.title),
      })
      .select()
      .maybeSingle();
    setSaving(false);
    if (err) { setError(err.message); return; }
    if (data) onCreated((data as { id: string }).id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-100 text-amber-600">
              <i className="ri-book-open-line text-lg"></i>
            </div>
            <div>
              <h2 className="text-slate-800 font-semibold text-base">Nuevo Libro de Firmas</h2>
              <p className="text-slate-400 text-xs">Crea el libro y luego sube las fotos</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-lg transition-colors cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Título *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="Ej: Libro de Firmas — Boda García & López"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>

          {/* Client */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Cliente *</label>
            <select
              value={form.client_id}
              onChange={(e) => set("client_id", e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
            >
              <option value="">Selecciona cliente...</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.full_name}</option>
              ))}
            </select>
          </div>

          {/* Contract + Job */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Contrato</label>
              <select
                value={form.contract_id}
                onChange={(e) => set("contract_id", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
              >
                <option value="">—</option>
                {contracts.map((c) => (
                  <option key={c.id} value={c.id}>{c.title || c.id.slice(0, 8)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Job / Sesión</label>
              <select
                value={form.job_id}
                onChange={(e) => set("job_id", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
              >
                <option value="">—</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>{j.title}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Event date + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Fecha del evento</label>
              <input
                type="date"
                value={form.event_date}
                onChange={(e) => set("event_date", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Estado inicial</label>
              <select
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
              >
                <option value="draft">Borrador</option>
                <option value="published">Publicado</option>
              </select>
            </div>
          </div>

          {/* Guest book toggle */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-amber-50 border border-amber-100">
            <div>
              <p className="text-sm font-medium text-slate-700">Habilitar Libro de Visitas</p>
              <p className="text-xs text-slate-400 mt-0.5">Los invitados podrán dejar mensajes sobre las fotos</p>
            </div>
            <button
              type="button"
              onClick={() => set("guest_book_enabled", !form.guest_book_enabled)}
              className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${form.guest_book_enabled ? "bg-amber-500" : "bg-slate-200"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.guest_book_enabled ? "translate-x-5" : "translate-x-0"}`} />
            </button>
          </div>

          {/* Closing message */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Mensaje de cierre</label>
            <textarea
              rows={2}
              maxLength={500}
              value={form.closing_message}
              onChange={(e) => set("closing_message", e.target.value)}
              placeholder="Ej: Gracias por ser parte de este día especial..."
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
            />
          </div>

          {error && (
            <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            Cancelar
          </button>
          <button
            onClick={handleSubmit as unknown as React.MouseEventHandler}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <i className="ri-add-line"></i>}
            {saving ? "Creando…" : "Crear Libro"}
          </button>
        </div>
      </div>
    </div>
  );
}